import { useState, useContext, useMemo, useCallback, useRef } from "react";
import { AuthContext } from "../contexts/AuthContext";
import { useAuthFetch } from "./useAuthFetch";

// Shared data + filtering for the top-bar search dropdown and the /search
// results page. Lists are fetched once (lazily, via ensureData) and filtered
// client-side as the query changes.
const useGlobalSearch = (query) => {
  const { authContext } = useContext(AuthContext);
  const orgId = authContext?.organization?.orgId;
  const authFetch = useAuthFetch();

  const [sites, setSites] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const hasFetchedRef = useRef(false);

  const ensureData = useCallback(async () => {
    if (hasFetchedRef.current || !orgId) return;
    hasFetchedRef.current = true;
    setIsLoading(true);

    const safeJson = async (response) => {
      if (!response.ok) {
        console.error("HTTP Error: ", response.statusText);
        return null;
      }
      return response.json();
    };

    const sitesPromise = authFetch(
      `${process.env.REACT_APP_API_URL}/v1/sites?org_id=${orgId}&customer_ids=&include_details=true`
    )
      .then(safeJson)
      .then((data) => {
        if (Array.isArray(data)) setSites(data);
      })
      .catch((error) => console.error("Fetching error: ", error.message));

    const customersPromise = authFetch(
      `${process.env.REACT_APP_API_URL}/v1/customers`
    )
      .then(safeJson)
      .then((data) => {
        if (Array.isArray(data)) setCustomers(data);
      })
      .catch((error) => console.error("Fetching error: ", error.message));

    const usersPromise = authFetch(
      `${process.env.REACT_APP_API_URL}/v1/users?org_id=${orgId}`
    )
      .then(safeJson)
      .then((data) => {
        if (data && Array.isArray(data.users)) setUsers(data.users);
      })
      .catch((error) => console.error("Fetching error: ", error.message));

    await Promise.all([sitesPromise, customersPromise, usersPromise]);
    setIsLoading(false);
  }, [orgId, authFetch]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const empty = { sites: [], customers: [], users: [] };
    if (!q) return empty;

    const matches = (value) => (value || "").toLowerCase().includes(q);

    return {
      sites: sites.filter(
        (site) =>
          matches(site.site_name) || matches(site.street)
      ),
      customers: customers.filter((customer) =>
        matches(customer.customer_name)
      ),
      users: users.filter(
        (user) =>
          matches(`${user.first_name || ""} ${user.last_name || ""}`) ||
          matches(user.email)
      ),
    };
  }, [query, sites, customers, users]);

  const totalCount =
    results.sites.length + results.customers.length + results.users.length;

  return { results, totalCount, isLoading, ensureData };
};

export { useGlobalSearch };
